// User Management Types
// Based on API documentation from /backend/USER_API_DOCUMENTATION.md

import React from 'react';

export interface User {
  userId: string;            // 使用者ID (唯一標識)
  userName: string;          // 使用者姓名
  email?: string;            // 電子郵件
  isActive: boolean;         // 是否啟用
  dptNo?: string;            // 部門編號
  positionId?: number;       // 職位ID（建立時指定）
  lastLoginAt?: string;      // 最後登入時間
  createdAt: string;         // 建立時間
  updatedAt: string;         // 更新時間
  
  // 登入安全相關
  failedLoginCount?: number; // 失敗登入次數
  lockTime?: string;         // 帳號鎖定時間
  lockReason?: string;       // 鎖定原因
  isLocked?: boolean;        // 是否被鎖定（計算出的狀態）
  
  // 關聯資料
  department?: Department;   // 所屬部門
  positions?: UserPosition[]; // 職位列表
  permissions?: UserPermission[]; // 權限列表
}

export interface Department {
  dptNo: string;             // 部門編號
  dptName: string;           // 部門名稱
  dptEName?: string;         // 部門英文名稱
  parentNo?: string;         // 上級部門編號
  manager?: string;          // 部門主管
  managerPositionId?: string; // 主管職位ID
  dptLevel: number;          // 部門層級
  isActive: boolean;         // 是否啟用
  canPay?: boolean;          // 是否可付款
  budUse?: boolean;          // 是否使用預算
  createDate?: string;       // 建立日期
  children?: Department[];   // 子部門 (樹狀結構)
}

export interface Position {
  positionId: number;        // 職位ID
  positionCode: string;      // 職位代碼
  positionName: string;      // 職位名稱
  positionEnName?: string;   // 職位英文名稱
  dptNo: string;             // 部門編號
  titleCode?: string;        // 職稱代碼
  planNum?: number;          // 計劃人數
  realNum?: number;          // 實際人數
  isActive: boolean;         // 是否啟用
  memo?: string;             // 備註
  createDate?: string;       // 建立日期
  department?: Department;   // 所屬部門
}

export interface UserPosition {
  positionId: number;        // 職位ID
  positionName: string;      // 職位名稱
  isMain: boolean;           // 是否主要職位
  position?: Position;       // 職位詳情
}

export interface UserPermission {
  programId: number;                // 程式ID
  programName: string;              // 程式名稱
  addNew: boolean | null;           // 新增權限 (null=未設定/繼承群組)
  edit: boolean | null;             // 編輯權限
  delete: boolean | null;           // 刪除權限
  export: boolean | null;           // 匯出權限
  print: boolean | null;            // 列印權限
  query: boolean | null;            // 查詢權限
  approve: boolean | null;          // 核准權限
}

export interface PermissionGroup {
  rGroupId: string;          // 群組ID
  rGroupName: string;        // 群組名稱
  isActive: boolean;         // 是否啟用
  createDate?: string;       // 建立日期
  memo?: string;             // 備註
  permissions?: GroupPermission[]; // 群組權限
  members?: User[];          // 群組成員（完整的使用者物件）
}

export interface GroupPermission {
  programId: number;         // 程式ID
  programName: string;       // 程式名稱
  addNew: boolean;           // 新增權限
  edit: boolean;             // 編輯權限
  delete: boolean;           // 刪除權限
  export: boolean;           // 匯出權限
  print: boolean;            // 列印權限
  query: boolean;            // 查詢權限
  approve: boolean;          // 核准權限
}

export interface Program {
  pgId: number;              // 程式ID
  pgCode: string;            // 程式代碼
  pgName: string;            // 程式名稱
  pgEnName?: string;         // 程式英文名稱
  pgParent?: number;         // 父程式ID
  pgType: string;            // 程式類型 (MENU/FORM)
  pgLevel: number;           // 程式層級
  isActive: boolean;         // 是否啟用
  children?: Program[];      // 子程式 (樹狀結構)
}

// API Request/Response Types

export interface UserListParams {
  page?: number;
  limit?: number;
  search?: string;           // 搜尋關鍵字(使用者ID或姓名)
  dptNo?: string;            // 部門篩選
  isActive?: boolean;        // 狀態篩選
  sortBy?: string;           // 排序欄位
  sortDesc?: boolean;        // 是否降序
}

export interface UserListResponse {
  items: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface UserCreateRequest {
  userId: string;            // 使用者ID (必填)
  userName: string;          // 使用者姓名 (必填)
  email?: string;            // 電子郵件
  dptNo?: string;            // 部門編號
  positionId?: number;       // 職位ID
  isMain?: boolean;          // 是否主要職位
  password: string;          // 密碼 (必填)
}

export interface UserUpdateRequest {
  userName?: string;         // 使用者姓名
  email?: string;            // 電子郵件
  dptNo?: string;            // 部門編號
  isActive?: boolean;        // 是否啟用
}

export interface UserStatusRequest {
  isActive: boolean;         // 是否啟用
  reason?: string;           // 原因
}

export interface UserPasswordRequest {
  newPassword: string;       // 新密碼
}

export interface UserPositionsRequest {
  positions: {
    positionId: number;
    isMain: boolean;
  }[];
}

export interface UserPermissionsRequest {
  permissions: {
    programId: number;
    addNew: boolean | null;
    edit: boolean | null;
    delete: boolean | null;
    export: boolean | null;
    print: boolean | null;
    query: boolean | null;
    approve: boolean | null;
  }[];
}

export interface DepartmentListParams {
  tree?: boolean;            // 是否以樹狀結構返回
  active?: boolean;          // 只取得啟用的部門
}

export interface DepartmentCreateRequest {
  dptNo: string;             // 部門編號 (必填)
  dptName: string;           // 部門名稱 (必填)
  dptEName?: string;         // 部門英文名稱
  parentNo?: string;         // 上級部門編號
  manager?: string;          // 部門主管
  managerPositionId?: string; // 主管職位ID
  dptLevel: number;          // 部門層級 (必填)
  canPay?: boolean;          // 是否可付款
  budUse?: boolean;          // 是否使用預算
}

export interface PositionListParams {
  page?: number;
  limit?: number;
  deptNo?: string;           // 部門編號篩選
  active?: boolean;          // 只取得啟用的職位
}

export interface PositionCreateRequest {
  positionCode: string;      // 職位代碼 (必填)
  positionName: string;      // 職位名稱 (必填)
  positionEnName?: string;   // 職位英文名稱
  dptNo: string;             // 部門編號 (必填)
  titleCode?: string;        // 職稱代碼
  planNum?: number;          // 計劃人數
  memo?: string;             // 備註
}

export interface PermissionGroupCreateRequest {
  rGroupId: string;          // 群組ID (必填)
  rGroupName: string;        // 群組名稱 (必填)
  memo?: string;             // 備註
}

export interface PermissionGroupUpdateRequest {
  rGroupName?: string;       // 群組名稱
  memo?: string;             // 備註
  isActive?: boolean;        // 是否啟用
}

// Authentication Types

export interface LoginRequest {
  userId: string;            // 使用者ID
  password: string;          // 密碼
  dbType: string;            // 資料庫類型
}

export interface LoginResponse {
  token: string;             // JWT 令牌
  refreshToken: string;      // 刷新令牌
  expiresAt: string;         // 過期時間
  firebaseCustomToken?: string; // Firebase Custom Token (signInWithCustomToken 用)
  user: {
    userId: string;
    userName: string;
    email?: string;
    isActive: boolean;
    dptNo?: string;
    department?: Department;
    positions?: UserPosition[];
  };
}

export interface RefreshTokenResponse {
  accessToken: string;
  expiresIn: number;
  firebaseCustomToken?: string;
}

export interface FirebaseTokenResponse {
  firebaseCustomToken: string;
}

export interface ChangePasswordRequest {
  oldPassword: string;       // 舊密碼
  newPassword: string;       // 新密碼
}

export interface RefreshTokenRequest {
  refreshToken: string;      // 刷新令牌
}

// UI State Types

export interface UserFormData extends UserCreateRequest {
  // Additional form-specific fields
}

export interface UserTableColumn {
  key: string;
  title: string;
  dataIndex: string;
  width?: number;
  sorter?: boolean;
  filterable?: boolean;
  render?: (value: any, record: User) => React.ReactNode;
}

// Statistics Types

export interface UserStatistics {
  total: number;             // 總使用者數
  active: number;            // 啟用使用者數
  inactive: number;          // 停用使用者數
  totalDepartments: number;  // 總部門數
  totalPositions: number;    // 總職位數
  totalPermissionGroups: number; // 總權限群組數
  onlineUsers?: number;      // 線上使用者數
}

// User Session Types for Monitoring

export interface UserSession {
  sessionId: string;         // 會話ID
  sessionToken: string;      // 會話令牌
  userId: string;            // 使用者ID
  userName: string;          // 使用者姓名
  loginTime: string;         // 登入時間
  lastActivityTime: string;  // 最後活動時間
  logoutTime?: string;       // 登出時間
  ipAddress: string;         // IP位址
  userAgent: string;         // 使用者代理
  deviceInfo?: string;       // 裝置資訊
  location?: string;         // 位置資訊
  isActive: boolean;         // 是否活躍
  timeoutWarned: boolean;    // 是否已逾時警告
  forceLogout: boolean;      // 是否強制登出
  logoutReason?: string;     // 登出原因
  department?: Department;   // 所屬部門
}

export interface LoginAttempt {
  attemptId: string;         // 嘗試ID
  userId: string;            // 使用者ID
  attemptTime: string;       // 嘗試時間
  ipAddress: string;         // IP位址
  userAgent: string;         // 使用者代理
  result: 'SUCCESS' | 'FAILED'; // 嘗試結果
  failureReason?: string;    // 失敗原因
  location?: string;         // 位置資訊
}

export interface OnlineUserMetrics {
  totalOnline: number;       // 總線上使用者數
  activeSessions: number;    // 活躍會話數
  newLoginsToday: number;    // 今日新登入數
  averageSessionTime: number; // 平均會話時間（分鐘）
  topDepartments: Array<{    // 最活躍部門
    dptName: string;
    userCount: number;
  }>;
  sessionsByHour: Array<{    // 每小時會話統計
    hour: number;
    count: number;
  }>;
}

export interface DepartmentStatistics {
  total: number;             // 總部門數
  active: number;            // 啟用部門數
  levels: number;            // 部門層級數
  totalUsers: number;        // 總使用者數
}

export interface PositionStatistics {
  total: number;             // 總職位數
  active: number;            // 啟用職位數
  totalPlanned: number;      // 計劃總人數
  totalActual: number;       // 實際總人數
}

// Error Response Type
export interface ErrorResponse {
  success: false;
  error: {
    code: string;
    message: string;
    details?: string;
  };
  timestamp: string;
  requestId?: string;
}

// Success Response Type
export interface SuccessResponse<T = any> {
  success: true;
  data: T;
  message: string;
  timestamp: string;
}

// Common API Response Type
export type ApiResponse<T = any> = SuccessResponse<T> | ErrorResponse;

// Permission Constants
export const PERMISSION_ACTIONS = {
  ADD_NEW: 'addNew',
  EDIT: 'edit',
  DELETE: 'delete',
  EXPORT: 'export',
  PRINT: 'print',
  QUERY: 'query',
  APPROVE: 'approve'
} as const;

export type PermissionAction = typeof PERMISSION_ACTIONS[keyof typeof PERMISSION_ACTIONS];

// Program Types
export const PROGRAM_TYPES = {
  MENU: 'MENU',
  FORM: 'FORM'
} as const;

export type ProgramType = typeof PROGRAM_TYPES[keyof typeof PROGRAM_TYPES];

// Status Display
export const StatusDisplay = {
  active: { text: '啟用', color: 'green' },
  inactive: { text: '停用', color: 'red' }
} as const;

// Department Level Colors
export const DepartmentLevelColors = {
  1: '#1890ff',    // 藍色 - 一級部門
  2: '#52c41a',    // 綠色 - 二級部門
  3: '#fa8c16',    // 橙色 - 三級部門
  4: '#722ed1',    // 紫色 - 四級部門
  default: '#8c8c8c' // 灰色 - 其他層級
} as const;

// V2 API Types
export interface EffectivePermission {
  pgId: number;              // 程式ID
  pgCode: string;            // 程式代碼
  pgName: string;            // 程式名稱
  pgParent?: number;         // 父程式ID
  groupPermission?: {        // 群組權限
    pgId: number;
    addNew?: boolean;
    edit?: boolean;
    del?: boolean;
    query?: boolean;
    export?: boolean;
    printer?: boolean;
  };
  personalPermission?: {     // 個人權限
    pgId: number;
    addNew?: boolean;
    edit?: boolean;
    del?: boolean;
    query?: boolean;
    export?: boolean;
    printer?: boolean;
  };
  finalPermission: {         // 最終有效權限
    pgId: number;
    addNew?: boolean;
    edit?: boolean;
    del?: boolean;
    query?: boolean;
    export?: boolean;
    printer?: boolean;
  };
}

export interface UserPermissionMatrix {
  userId: string;
  userName: string;
  groups: Array<{
    groupId: string;
    groupName: string;
    permissions: any[];
  }>;
  personalPermissions: any[];
  effectivePermissions: EffectivePermission[];
}

export interface PermissionAnalysis {
  userId: string;
  userName: string;
  summary: {
    totalPrograms: number;
    groupPermissionCount: number;
    personalPermissionCount: number;
    effectivePermissionCount: number;
  };
  conflicts: Array<{
    programId: number;
    programName: string;
    groupPermission: any;
    personalPermission: any;
    resolution: string;
  }>;
  recommendations?: string[];
}

export interface GroupMembers {
  groupId: string;
  groupName: string;
  members: Array<{
    userId: string;
    userName: string;
    email?: string;
    department?: string;
    isActive: boolean;
  }>;
  total: number;
}

export interface GroupPermissions {
  groupId: string;
  groupName: string;
  permissions: Array<{
    programId: number;
    programName: string;
    addNew: boolean;
    edit: boolean;
    del: boolean;
    query: boolean;
    export: boolean;
    printer: boolean;
  }>;
}

export interface ProgramTree {
  pgId: number;
  pgCode: string;
  pgName: string;
  pgParent?: number;
  pgType: string;
  pgLevel: number;
  isActive: boolean;
  hasPermission?: boolean;
  children?: ProgramTree[];
}

// Form validation rules
export const ValidationRules = {
  userId: {
    required: true,
    pattern: /^[a-zA-Z0-9_]{3,20}$/,
    message: '使用者ID必須為3-20位英數字或底線'
  },
  userName: {
    required: true,
    maxLength: 30,
    message: '使用者姓名為必填項目，最多30個字符'
  },
  email: {
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    message: '請輸入有效的電子郵件地址'
  },
  password: {
    required: true,
    minLength: 8,
    pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/,
    message: '密碼必須至少8位，包含大小寫字母和數字'
  },
  dptNo: {
    pattern: /^[A-Z0-9]{1,20}$/,
    message: '部門編號必須為大寫英數字，最多20位'
  },
  positionCode: {
    required: true,
    pattern: /^[A-Z0-9_]{1,30}$/,
    message: '職位代碼為必填項目，必須為大寫英數字或底線'
  }
} as const;